/**
 * 「排列」工具的定义 —— **纯数据**，右键菜单照着它渲染图标行。
 *
 * ## 为什么单独一个文件
 *
 * 这些东西（有哪些对齐方式、分几组、每组什么图标）是**产品定义**，
 * 和「谁来执行、什么时候禁用」是两件事。原来它们全塞在 `useMenus` 里，
 * 于是：
 *   · 想验「8 种对齐是不是都上了菜单」得先造一个巨大的菜单上下文；
 *   · 想调图标得在一千多行的 composable 里翻。
 *
 * 拆出来之后这一层是纯数据，可以直接断言 —— 其中最有用的一条是
 * **每个 `AlignMode` / `SizeMode` 恰好出现一次**：
 * 新增一种对齐方式却忘了加进菜单，会在这里挂，而不是等用户发现
 * 「咦怎么没有等宽」。
 *
 * 分组按用户的要求是**三组**：
 *   对齐(6) / 分布(2) / 尺寸(2)
 * 前两组改位置、第三组改尺寸 —— 这也是为什么尺寸不和前两组混在一起。
 */
import type { AlignMode, SizeMode } from './nodeAlign'

/**
 * 图标：统一 24×24 viewBox、只用描边。
 *
 * ⚠️ 颜色交给 `currentColor`：`NpMenu` 用 `v-html` 塞进来，
 * 颜色靠 CSS 的 `color` 继承，这样禁用态（灰）和危险态（红）
 * 不用各备一套图标。
 */
export const ARRANGE_ICONS = {
  /* 对齐：一条基准线 + 两个贴着它的方块 */
  left: '<path d="M3 4v16"/><rect x="7" y="6" width="12" height="4" rx="1"/><rect x="7" y="14" width="7" height="4" rx="1"/>',
  hcenter:
    '<path d="M12 3v18"/><rect x="5" y="6" width="14" height="4" rx="1"/><rect x="8" y="14" width="8" height="4" rx="1"/>',
  right:
    '<path d="M21 4v16"/><rect x="5" y="6" width="12" height="4" rx="1"/><rect x="10" y="14" width="7" height="4" rx="1"/>',
  top: '<path d="M4 3h16"/><rect x="6" y="7" width="4" height="12" rx="1"/><rect x="14" y="7" width="4" height="7" rx="1"/>',
  vcenter:
    '<path d="M3 12h18"/><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="8" width="4" height="8" rx="1"/>',
  bottom:
    '<path d="M4 21h16"/><rect x="6" y="5" width="4" height="12" rx="1"/><rect x="14" y="10" width="4" height="7" rx="1"/>',
  /* 分布：两端贴边 + 中间的方块等距 */
  distH: '<path d="M3 4v16M21 4v16"/><rect x="9" y="7" width="6" height="10" rx="1"/>',
  distV: '<path d="M4 3h16M4 21h16"/><rect x="7" y="9" width="10" height="6" rx="1"/>',
  /* 尺寸：等宽 / 等高 —— 用「尺寸标注」的箭头表达，不用方块 */
  sizeW: '<path d="M4 12h16"/><path d="M4 8v8M20 8v8"/><rect x="8" y="9" width="8" height="6" rx="1"/>',
  sizeH: '<path d="M12 4v16"/><path d="M8 4h8M8 20h8"/><rect x="9" y="8" width="6" height="8" rx="1"/>',
} as const

export interface ArrangeTool<M extends AlignMode | SizeMode = AlignMode | SizeMode> {
  /** 菜单项 id 的后半段，拼成 `align-left` / `size-hsize` */
  mode: M
  label: string
  icon: string
}

export interface ArrangeGroup {
  id: string
  /** 这一组是改位置还是改尺寸 —— 菜单里只用来拼 id，不决定渲染 */
  kind: 'align' | 'distribute' | 'size'
  tools: ArrangeTool[]
}

/**
 * 工具分组。顺序就是菜单里的顺序。
 *
 * ⚠️ `align` 是 `AlignMode`（改位置），`size` 是 `SizeMode`（改尺寸）。
 * 混**同一条**定义里会让「点下去是挪位置还是改大小」变得要靠图标猜 ——
 * 所以数据上仍然是两组。
 *
 * ⚠️ 但**渲染**时 `distribute` 和 `size` 会被并成一行
 * （见 `ARRANGE_ROWS`）：两组各 2 个按钮、各占一行的话，
 * 一行里两个方块两边全是空白，用户实测的原话是「把两行两个的放一行吧，
 * 不然浪费」。
 */
export const ARRANGE_GROUPS: readonly ArrangeGroup[] = [
  {
    id: 'arrange-align',
    kind: 'align',
    tools: [
      { mode: 'left', label: '左对齐', icon: ARRANGE_ICONS.left },
      { mode: 'hcenter', label: '水平居中', icon: ARRANGE_ICONS.hcenter },
      { mode: 'right', label: '右对齐', icon: ARRANGE_ICONS.right },
      { mode: 'top', label: '顶对齐', icon: ARRANGE_ICONS.top },
      { mode: 'vcenter', label: '垂直居中', icon: ARRANGE_ICONS.vcenter },
      { mode: 'bottom', label: '底对齐', icon: ARRANGE_ICONS.bottom },
    ],
  },
  {
    id: 'arrange-distribute',
    kind: 'distribute',
    tools: [
      { mode: 'hdistribute', label: '水平等间隙', icon: ARRANGE_ICONS.distH },
      { mode: 'vdistribute', label: '垂直等间隙', icon: ARRANGE_ICONS.distV },
    ],
  },
  {
    id: 'arrange-size',
    kind: 'size',
    tools: [
      { mode: 'hsize', label: '等宽', icon: ARRANGE_ICONS.sizeW },
      { mode: 'vsize', label: '等高', icon: ARRANGE_ICONS.sizeH },
    ],
  },
]

/**
 * 菜单里实际渲染的**行**。
 *
 * ⚠️ `distribute` + `size` 合成一行（4 个按钮）—— 见 `ARRANGE_GROUPS`
 * 的注释。分组的**语义**没有合并（数据和测试仍按三组看），
 * 合并的只是**视觉行**。
 */
export function arrangeRows(): { id: string; tools: ArrangeTool[] }[] {
  const rows: { id: string; tools: ArrangeTool[] }[] = []
  let pending: ArrangeTool[] = []
  for (const group of ARRANGE_GROUPS) {
    if (group.kind === 'align') {
      if (pending.length) { rows.push({ id: 'arrange-compact', tools: pending }); pending = [] }
      rows.push({ id: group.id, tools: [...group.tools] })
      continue
    }
    /* 分布 + 尺寸：攒起来，最后并成一行 */
    pending.push(...group.tools)
  }
  if (pending.length) rows.push({ id: 'arrange-compact', tools: pending })
  return rows
}

/** 拍平出来的 `AlignMode` 清单（测试用：应当**恰好**覆盖全部对齐方式） */
export const ARRANGE_ALIGN_MODES: readonly AlignMode[] = ARRANGE_GROUPS.filter(
  (group) => group.kind !== 'size',
).flatMap((group) => group.tools.map((tool) => tool.mode as AlignMode))

/** 拍平出来的 `SizeMode` 清单 */
export const ARRANGE_SIZE_MODES: readonly SizeMode[] = ARRANGE_GROUPS.filter(
  (group) => group.kind === 'size',
).flatMap((group) => group.tools.map((tool) => tool.mode as SizeMode))
